import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { API, BACKEND_URL } from '../utils/constants';
import { formatCurrency } from '../utils/helpers';

const fmtDate = (d) => {
  if (!d) return '';
  const str = typeof d === 'string' ? d : d.toISOString();
  const parts = str.split('T')[0].split('-');
  const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const PublicEstimateView = ({ publicToken }) => {
  const [estimate, setEstimate] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const cacheBuster = searchParams.get('t') || searchParams.get('v');
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const approvedDate = estimate?.approved_at ? new Date(estimate.approved_at).toLocaleDateString() : '';
  const signatureCanvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Always add cache-buster and no-cache request headers to prevent stale cached responses
      const params = new URLSearchParams();
      params.set('t', cacheBuster || Date.now().toString());
      if (isPreviewMode) params.set('mode', 'preview');
      const noCacheHeaders = {
        'Cache-Control': 'no-cache, no-store, max-age=0',
        Pragma: 'no-cache',
      };
      const estimateUrl = `${API}/sales/estimates/public/${publicToken}?${params.toString()}`;
      const estimateRefreshUrl = `${API}/sales/estimates/public/${publicToken}/refresh?${params.toString()}`;
      const estimatePromise = axios.get(estimateUrl, { headers: noCacheHeaders }).catch(async () => {
        return axios.get(estimateRefreshUrl, { headers: noCacheHeaders });
      });
      const [estimateRes, settingsRes] = await Promise.allSettled([
        estimatePromise,
        axios.get(`${API}/sales/public-settings`)
      ]);
      if (estimateRes.status !== 'fulfilled') {
        throw estimateRes.reason || new Error('Failed to load estimate');
      }
      setEstimate(estimateRes.value.data);
      setSettings(settingsRes.status === 'fulfilled' ? settingsRes.value.data : {});
      setSignatureName(estimateRes.value.data?.client_name || '');
    } catch (err) {
      console.error('Error loading public estimate:', err);
      setError('Unable to load this estimate.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [publicToken]);

  const initSignatureCanvas = React.useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (showSignatureModal) {
      initSignatureCanvas();
    }
  }, [showSignatureModal, initSignatureCanvas]);

  const getCanvasPoint = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    return { x, y };
  };

  const handleSignatureStart = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    if (!point) return;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const handleSignatureMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    if (!point) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const handleSignatureEnd = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    setSignatureData(dataUrl);
  };

  const handleSignatureClear = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  const handleApprove = async () => {
    if (!signatureData) {
      alert('Please sign before submitting.');
      return;
    }
    try {
      await axios.post(`${API}/sales/estimates/${estimate.id}/approve`, {
        client_name: signatureName,
        signature_data: signatureData
      });
      alert('Estimate approved. Thank you!');
      setShowSignatureModal(false);
      setSignatureData('');
      await loadData();
    } catch (err) {
      console.error('Error approving estimate:', err);
      alert('Unable to approve estimate. Please try again.');
    }
  };

  const handleDecline = async () => {
    try {
      await axios.post(`${API}/sales/estimates/${estimate.id}/decline`, {
        reason: declineReason
      });
      alert('Estimate declined.');
      setShowDeclineModal(false);
      setDeclineReason('');
      await loadData();
    } catch (err) {
      console.error('Error declining estimate:', err);
      alert('Unable to decline estimate.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-estimate-loading">
        <div className="text-lg text-gray-600">Loading estimate...</div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-estimate-error">
        <div className="text-lg text-red-600">{error || 'Estimate not found'}</div>
      </div>
    );
  }

  const lineItems = estimate.line_items || [];
  const getLineItemTotal = (item) => {
    const quantity = Number(item?.quantity || 0);
    const unitCost = Number(item?.unit_cost || 0);
    const markup = Number(item?.markup_percentage || 0);
    const baseTotal = quantity * unitCost;
    const computedTotal = baseTotal * (1 + markup / 100);
    const totalValue = Number(item?.total);
    const hasInputs = quantity > 0 || unitCost > 0;
    if (!Number.isFinite(totalValue) || (totalValue === 0 && hasInputs)) {
      return computedTotal;
    }
    return totalValue;
  };
  const getDescriptionLines = (description) => {
    const lines = (description || '').split('\n');
    return lines.length > 0 ? lines : [''];
  };
  const preparedForCityLine = [estimate.client_city, estimate.client_province].filter(Boolean).join(', ');
  const companyCityLine = [settings?.company_city, settings?.company_province].filter(Boolean).join(', ');
  const discountAmount = Number(estimate.discount_amount || 0);
  const markupAmount = Number(estimate.markup_amount || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg px-6 sm:px-10 py-8" data-testid="public-estimate-document">
          <div className="text-center text-xs tracking-[0.4em] text-gray-400 font-semibold" data-testid="public-estimate-document-title">
            ESTIMATE
          </div>
          <div className="mt-6 flex flex-col md:flex-row md:justify-between gap-8">
            <div className="flex-1">
              <div className="mb-4">
                <img
                  src={`${API}/sales/logo`}
                  alt={`${settings?.company_name || 'Company'} Logo`}
                  className="h-12 object-contain"
                  data-testid="public-estimate-company-logo"
                />
              </div>
              <div className="text-sm text-gray-800 leading-relaxed" data-testid="public-estimate-company-info">
                <div className="font-semibold" data-testid="public-estimate-company-name">{settings?.company_name}</div>
                <div data-testid="public-estimate-company-address">{settings?.company_address}</div>
                <div data-testid="public-estimate-company-location">
                  {companyCityLine}{settings?.company_postal_code ? ` ${settings?.company_postal_code}` : ''}
                </div>
                <div data-testid="public-estimate-company-phone">Phone: {settings?.company_phone}</div>
                <div data-testid="public-estimate-company-email">Email: {settings?.company_email}</div>
                <div data-testid="public-estimate-company-web">Web: {settings?.company_website}</div>
              </div>
            </div>
            <div className="w-full md:w-72 text-right">
              <div className="text-xs uppercase tracking-widest text-gray-500" data-testid="public-estimate-prepared-label">Prepared For</div>
              <div className="mt-2 text-sm text-gray-800 leading-relaxed" data-testid="public-estimate-prepared-for">
                <div className="font-semibold" data-testid="public-estimate-client-name">{estimate.client_name}</div>
                <div data-testid="public-estimate-client-address">{estimate.client_address}</div>
                <div data-testid="public-estimate-client-location">{preparedForCityLine}</div>
                <div data-testid="public-estimate-client-phone">{estimate.client_phone}</div>
              </div>
              <div className="mt-4 text-sm text-gray-700" data-testid="public-estimate-meta">
                <div className="flex justify-between border-b border-gray-200 pb-1" data-testid="public-estimate-meta-number">
                  <span className="text-gray-500">Estimate #</span>
                  <span className="font-medium text-gray-900">{estimate.estimate_number}</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 py-1" data-testid="public-estimate-meta-date">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-gray-900">{estimate.date}</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 py-1" data-testid="public-estimate-meta-expiry">
                  <span className="text-gray-500">Expiry Date</span>
                  <span className="font-medium text-gray-900">{estimate.expiration_date || ''}</span>
                </div>
                <div className="flex justify-between pt-1" data-testid="public-estimate-meta-tax">
                  <span className="text-gray-500">Business / Tax #</span>
                  <span className="font-medium text-gray-900">{settings?.hst_number}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8" data-testid="public-estimate-line-items">
            <div className="grid grid-cols-[1fr_140px] gap-4 text-sm font-semibold text-gray-900 border-b border-gray-900 pb-2" data-testid="public-estimate-line-items-header">
              <div>Description</div>
              <div className="text-right">Total</div>
            </div>
            <div className="divide-y divide-gray-200">
              {lineItems.length === 0 && (
                <div className="py-4 text-sm text-gray-500" data-testid="public-estimate-no-line-items">No line items.</div>
              )}
              {lineItems.map((item, index) => {
                const lineTotal = getLineItemTotal(item);
                const descriptionLines = getDescriptionLines(item.description || '');
                return (
                  <div key={item.id || index} className="py-3" data-testid={`public-estimate-item-${index}`}>
                    {descriptionLines.map((line, lineIndex) => {
                      const lineText = line.trim() === '' ? '\u00A0' : line;
                      return (
                        <div key={`${item.id || index}-${lineIndex}`} className="grid grid-cols-[1fr_140px] gap-4">
                          <div
                            className={`${lineIndex === 0 ? 'font-semibold text-gray-900' : 'text-xs text-gray-500'} whitespace-pre-wrap`}
                            data-testid={`public-estimate-item-line-${index}-${lineIndex}`}
                          >
                            {lineText}
                          </div>
                          <div
                            className="text-right text-sm text-gray-900"
                            data-testid={`public-estimate-item-total-${index}-${lineIndex}`}
                          >
                            {lineIndex === 0 ? `$${formatCurrency(lineTotal)}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <div className="w-full sm:w-64 text-sm text-gray-700" data-testid="public-estimate-totals">
              <div className="flex justify-between" data-testid="public-estimate-subtotal">
                <span>Subtotal</span>
                <span>${formatCurrency(estimate.subtotal)}</span>
              </div>
              {markupAmount > 0 && (
                <div className="flex justify-between mt-1" data-testid="public-estimate-markup">
                  <span>Markup</span>
                  <span>${formatCurrency(markupAmount)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between mt-1" data-testid="public-estimate-discount">
                  <span>Discount</span>
                  <span>-${formatCurrency(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between mt-1" data-testid="public-estimate-tax">
                <span>Tax</span>
                <span>${formatCurrency(estimate.tax_amount)}</span>
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t border-gray-900 font-semibold text-gray-900" data-testid="public-estimate-total">
                <span>Total</span>
                <span>${formatCurrency(estimate.total)}</span>
              </div>
            </div>
          </div>

          {estimate.photos?.length > 0 && (
            <div className="mt-8" data-testid="public-estimate-photos">
              <h2 className="text-sm font-semibold text-gray-700 mb-3" data-testid="public-estimate-photos-title">Photos</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {estimate.photos.map(photo => (
                  <div key={photo.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`public-estimate-photo-${photo.id}`}>
                    <img
                      src={`${API}/sales/estimates/photos/${photo.id}`}
                      alt={photo.filename}
                      className="w-full h-28 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {estimate.documents?.length > 0 && (
            <div className="mt-8" data-testid="public-estimate-documents">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Documents</h2>
              <div className="space-y-2">
                {estimate.documents.map(doc => (
                  <a
                    key={doc.id}
                    href={`${API}/sales/estimates/documents/${doc.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors"
                    data-testid={`public-doc-${doc.id}`}
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">
                      {(doc.filename || '').split('.').pop().toUpperCase().slice(0, 4)}
                    </span>
                    <span className="text-sm text-blue-700 font-medium truncate">{doc.filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}


          {(estimate.notes || estimate.contract) && (
            <div className="mt-8" data-testid="public-estimate-notes">
              {estimate.notes && (
                <div className="mb-6">
                  <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2" data-testid="public-estimate-notes-title">Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="public-estimate-notes-text">{estimate.notes}</p>
                </div>
              )}
              {estimate.contract && (
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2" data-testid="public-estimate-contract-title">Contract Terms</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="public-estimate-contract-text">{estimate.contract}</p>
                </div>
              )}
            </div>
          )}

          {estimate.status === 'APPROVED' && estimate.client_signature && (
            <div className="mt-8" data-testid="public-estimate-signature">
              <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Client Signature</h3>
              <img src={estimate.client_signature} alt="Client Signature" className="h-16" />
              {estimate.client_signature_name && (
                <p className="text-sm text-gray-700 mt-2" data-testid="public-estimate-signature-name">
                  {estimate.client_signature_name}
                </p>
              )}
              {approvedDate && (
                <p className="text-xs text-gray-500 mt-1" data-testid="public-estimate-signature-date">
                  Signed and accepted on {approvedDate}
                </p>
              )}
            </div>
          )}
        </div>

        {!isPreviewMode && (
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={() => window.open(`${API}/sales/estimates/public/${publicToken}/pdf`, '_blank')}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold"
              data-testid="public-estimate-download-pdf"
            >
              Download PDF
            </button>
            {estimate.status !== 'APPROVED' && estimate.status !== 'DECLINED' && (
              <>
                <button
                  onClick={() => setShowSignatureModal(true)}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="public-estimate-accept"
                >
                  Accept & Sign
                </button>
                <button
                  onClick={() => setShowDeclineModal(true)}
                  className="px-5 py-2 border border-red-300 text-red-600 rounded-lg font-semibold"
                  data-testid="public-estimate-decline"
                >
                  Decline
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="public-estimate-sign-modal">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Sign to Approve</h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="public-estimate-sign-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="public-estimate-sign-name"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Signature</label>
                  <button
                    onClick={handleSignatureClear}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    data-testid="public-estimate-sign-clear"
                  >
                    Clear
                  </button>
                </div>
                <div className="border border-dashed border-gray-300 rounded-lg p-2 mt-2">
                  <canvas
                    ref={signatureCanvasRef}
                    width={600}
                    height={200}
                    onPointerDown={handleSignatureStart}
                    onPointerMove={handleSignatureMove}
                    onPointerUp={handleSignatureEnd}
                    onPointerLeave={handleSignatureEnd}
                    onPointerCancel={handleSignatureEnd}
                    className="w-full h-48 rounded-md bg-white"
                    style={{ touchAction: 'none' }}
                    data-testid="public-estimate-sign-canvas"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="public-estimate-sign-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="public-estimate-sign-submit"
                >
                  Approve Estimate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="public-estimate-decline-modal">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Decline Estimate</h3>
              <button
                onClick={() => setShowDeclineModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="public-estimate-decline-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <textarea
                  rows={3}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="public-estimate-decline-reason"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeclineModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="public-estimate-decline-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
                  data-testid="public-estimate-decline-submit"
                >
                  Decline Estimate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Invoice View (Customer-facing)
const PublicInvoiceView = ({ publicToken }) => {
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const cacheBuster = searchParams.get('t') || searchParams.get('v');
  const isPreviewMode = searchParams.get('mode') === 'preview';

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Always add cache-buster and no-cache request headers to prevent stale cached responses
      const params = new URLSearchParams();
      params.set('t', cacheBuster || Date.now().toString());
      if (isPreviewMode) params.set('mode', 'preview');
      const noCacheHeaders = {
        'Cache-Control': 'no-cache, no-store, max-age=0',
        Pragma: 'no-cache',
      };
      const invoiceUrl = `${API}/sales/invoices/public/${publicToken}?${params.toString()}`;
      const invoiceRefreshUrl = `${API}/sales/invoices/public/${publicToken}/refresh?${params.toString()}`;
      const invoicePromise = axios.get(invoiceUrl, { headers: noCacheHeaders }).catch(async () => {
        return axios.get(invoiceRefreshUrl, { headers: noCacheHeaders });
      });
      const [invoiceRes, settingsRes] = await Promise.allSettled([
        invoicePromise,
        axios.get(`${API}/sales/public-settings`)
      ]);
      if (invoiceRes.status !== 'fulfilled') {
        throw invoiceRes.reason || new Error('Failed to load invoice');
      }
      setInvoice(invoiceRes.value.data);
      setSettings(settingsRes.status === 'fulfilled' ? settingsRes.value.data : {});
    } catch (err) {
      console.error('Error loading public invoice:', err);
      if (err?.response?.status === 410) {
        setError('This invoice may have been updated. Please try refreshing.');
      } else {
        setError('Unable to load this invoice.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [publicToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-invoice-loading">
        <div className="text-lg text-gray-600">Loading invoice...</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-invoice-error">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">{error || 'Invoice not found'}</div>
          <button
            onClick={() => {
              setError('');
              setLoading(true);
              loadData();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            data-testid="public-invoice-retry"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const lineItems = invoice.line_items || [];
  const regularItems = lineItems.filter((item) => !item.is_change_order);
  const changeOrders = lineItems.filter((item) => item.is_change_order);
  const getLineItemTotal = (item) => {
    const quantity = Number(item?.quantity || 0);
    const unitCost = Number(item?.unit_cost || 0);
    const markup = Number(item?.markup_percentage || 0);
    const baseTotal = quantity * unitCost;
    const computedTotal = baseTotal * (1 + markup / 100);
    const totalValue = Number(item?.total);
    const hasInputs = quantity > 0 || unitCost > 0;
    if (!Number.isFinite(totalValue) || (totalValue === 0 && hasInputs)) {
      return computedTotal;
    }
    return totalValue;
  };
  const getDescriptionLines = (description) => {
    const lines = (description || '').split('\n');
    return lines.length > 0 ? lines : [''];
  };
  const preparedForCityLine = [invoice.client_city, invoice.client_province].filter(Boolean).join(', ');
  const companyCityLine = [settings?.company_city, settings?.company_province].filter(Boolean).join(', ');
  const invoiceDate = invoice.invoice_date || invoice.date || '';
  const discountAmount = Number(invoice.discount_amount || 0);
  const markupAmount = Number(invoice.markup_amount || 0);
  const scheduleItems = invoice.payment_schedule || [];
  const scheduleType = invoice.payment_schedule_type || 'percent';
  const hasSchedule = scheduleItems.length > 0;

  // FIFO allocation: apply amount_paid sequentially across milestones (with CC fee tracking)
  const scheduleAllocations = (() => {
    let remaining = parseFloat(invoice.amount_paid || 0);
    const total = parseFloat(invoice.total || 0);
    const schedPayments = (invoice.payments || [])
      .filter(p => !String(p.method || '').startsWith('Schedule:'))
      .map(p => ({
        left: parseFloat(p.amount || 0),
        ccFee: p.cc_fee_enabled ? (parseFloat(p.cc_fee_amount || 0)) : 0,
        total: Math.max(parseFloat(p.amount || 0), 0.01)
      }));
    let pmtPtr = 0;
    return scheduleItems.map(item => {
      const due = scheduleType === 'percent'
        ? total * (parseFloat(item.amount || 0) / 100)
        : parseFloat(item.amount || 0);
      const paid = Math.min(due, remaining);
      remaining = Math.max(0, remaining - paid);
      let ccFeeForEntry = 0;
      let need = paid;
      let i = pmtPtr;
      while (need > 0.001 && i < schedPayments.length) {
        const sp = schedPayments[i];
        const use = Math.min(need, sp.left);
        ccFeeForEntry += (use / sp.total) * sp.ccFee;
        need -= use;
        sp.left -= use;
        if (sp.left <= 0.001) i++;
      }
      pmtPtr = i;
      return { ...item, due, paidAmount: paid, balance: due - paid, ccFeeFromPayment: Math.round(ccFeeForEntry * 100) / 100 };
    });
  })();

  const nextUnpaidEntry = scheduleAllocations.find(a => a.balance > 0);
  const nextPaymentName = nextUnpaidEntry?.name || '';
  const getPublicSchedulePayment = (item) => {
    if (!item) return null;
    const payments = invoice.payments || [];
    if (item.payment_id) {
      const direct = payments.find(payment => payment.id === item.payment_id);
      if (direct) return direct;
    }
    const scheduleKey = item.id ? `schedule:${item.id}` : '';
    const byNote = scheduleKey ? payments.find(payment => (payment.notes || '').includes(scheduleKey)) : null;
    if (byNote) return byNote;
    const methodLabel = `Schedule: ${item.name || ''}`.trim();
    return payments.find(payment => payment.method === methodLabel) || null;
  };
  const renderScheduleAmount = (item) => {
    const rawValue = Number(item.amount || 0);
    const computed = scheduleType === 'percent'
      ? (Number(invoice.total || 0) * rawValue) / 100
      : rawValue;
    if (scheduleType === 'percent') {
      return `${rawValue.toFixed(2)}% ($${formatCurrency(computed)})`;
    }
    return `$${formatCurrency(computed)}`;
  };

  const renderLineItems = (items, sectionTitle, testId) => (
    <div className="mt-6" data-testid={testId}>
      {sectionTitle && (
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2" data-testid={`${testId}-title`}>
          {sectionTitle}
        </div>
      )}
      <div className="grid grid-cols-[1fr_140px] gap-4 text-sm font-semibold text-gray-900 border-b border-gray-900 pb-2" data-testid={`${testId}-header`}>
        <div>Description</div>
        <div className="text-right">Total</div>
      </div>
      <div className="divide-y divide-gray-200">
        {items.length === 0 && (
          <div className="py-4 text-sm text-gray-500" data-testid={`${testId}-empty`}>
            No line items.
          </div>
        )}
        {items.map((item, index) => {
          const lineTotal = getLineItemTotal(item);
          const descriptionLines = getDescriptionLines(item.description || '');
          return (
            <div key={item.id || index} className="py-3" data-testid={`${testId}-item-${index}`}>
              {descriptionLines.map((line, lineIndex) => {
                const lineText = line.trim() === '' ? '\u00A0' : line;
                return (
                  <div key={`${item.id || index}-${lineIndex}`} className="grid grid-cols-[1fr_140px] gap-4">
                    <div
                      className={`${lineIndex === 0 ? 'font-semibold text-gray-900' : 'text-xs text-gray-500'} whitespace-pre-wrap`}
                      data-testid={`${testId}-item-line-${index}-${lineIndex}`}
                    >
                      {lineText}
                    </div>
                    <div
                      className="text-right text-sm text-gray-900"
                      data-testid={`${testId}-item-total-${index}-${lineIndex}`}
                    >
                      {lineIndex === 0 ? `$${formatCurrency(lineTotal)}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg px-6 sm:px-10 py-8" data-testid="public-invoice-document">
          <div className="text-center text-xs tracking-[0.4em] text-gray-400 font-semibold" data-testid="public-invoice-document-title">
            INVOICE
          </div>
          <div className="mt-6 flex flex-col md:flex-row md:justify-between gap-8">
            <div className="flex-1">
              <div className="mb-4">
                <img
                  src={`${API}/sales/logo`}
                  alt={`${settings?.company_name || 'Company'} Logo`}
                  className="h-12 object-contain"
                  data-testid="public-invoice-company-logo"
                />
              </div>
              <div className="text-sm text-gray-800 leading-relaxed" data-testid="public-invoice-company-info">
                <div className="font-semibold" data-testid="public-invoice-company-name">{settings?.company_name}</div>
                <div data-testid="public-invoice-company-address">{settings?.company_address}</div>
                <div data-testid="public-invoice-company-location">
                  {companyCityLine}{settings?.company_postal_code ? ` ${settings?.company_postal_code}` : ''}
                </div>
                <div data-testid="public-invoice-company-phone">Phone: {settings?.company_phone}</div>
                <div data-testid="public-invoice-company-email">Email: {settings?.company_email}</div>
                <div data-testid="public-invoice-company-web">Web: {settings?.company_website}</div>
              </div>
            </div>
            <div className="w-full md:w-72 text-right">
              <div className="text-xs uppercase tracking-widest text-gray-500" data-testid="public-invoice-prepared-label">Prepared For</div>
              <div className="mt-2 text-sm text-gray-800 leading-relaxed" data-testid="public-invoice-prepared-for">
                <div className="font-semibold" data-testid="public-invoice-client-name">{invoice.client_name}</div>
                <div data-testid="public-invoice-client-address">{invoice.client_address}</div>
                <div data-testid="public-invoice-client-location">{preparedForCityLine}</div>
                <div data-testid="public-invoice-client-phone">{invoice.client_phone}</div>
              </div>
              <div className="mt-4 text-sm text-gray-700" data-testid="public-invoice-meta">
                <div className="flex justify-between border-b border-gray-200 pb-1" data-testid="public-invoice-meta-number">
                  <span className="text-gray-500">Invoice #</span>
                  <span className="font-medium text-gray-900">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 py-1" data-testid="public-invoice-meta-date">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-gray-900">{invoiceDate}</span>
                </div>
                <div className="flex justify-between pt-1" data-testid="public-invoice-meta-tax">
                  <span className="text-gray-500">Business / Tax #</span>
                  <span className="font-medium text-gray-900">{settings?.hst_number}</span>
                </div>
              </div>
            </div>
          </div>

          {renderLineItems(regularItems, null, 'public-invoice-line-items')}
          {changeOrders.length > 0 && renderLineItems(changeOrders, 'Change Orders', 'public-invoice-change-orders')}

          <div className="mt-6 flex justify-end">
            <div className="w-full sm:w-72 text-sm text-gray-700" data-testid="public-invoice-totals">
              <div className="flex justify-between" data-testid="public-invoice-subtotal">
                <span>Subtotal</span>
                <span>${formatCurrency(invoice.subtotal)}</span>
              </div>
              {markupAmount > 0 && (
                <div className="flex justify-between mt-1" data-testid="public-invoice-markup">
                  <span>Markup</span>
                  <span>${formatCurrency(markupAmount)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between mt-1" data-testid="public-invoice-discount">
                  <span>Discount</span>
                  <span>-${formatCurrency(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between mt-1" data-testid="public-invoice-tax">
                <span>Tax</span>
                <span>${formatCurrency(invoice.tax_amount)}</span>
              </div>
              {/* invoice.cc_fee_enabled removed — fees are now tracked per-payment */}
              <div className="flex justify-between mt-2 pt-2 border-t border-gray-900 font-semibold text-gray-900" data-testid="public-invoice-total">
                <span>Total</span>
                <span>${formatCurrency(invoice.total)}</span>
              </div>
            </div>
          </div>

          {/* Payment Summary - actual payments received */}
          {(() => {
            const realPayments = (invoice.payments || []).filter(p => !String(p.method || '').startsWith('Schedule:'));
            if (realPayments.length === 0) return null;
            return (
              <div className="mt-8" data-testid="public-invoice-payment-summary">
                <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Payment Summary</h3>
                <div className="grid grid-cols-[1fr_140px_120px] gap-3 text-xs font-semibold text-gray-500 border-b border-gray-200 pb-2">
                  <span>Date</span>
                  <span>Method</span>
                  <span className="text-right">Amount</span>
                </div>
                <div className="divide-y divide-gray-200">
                  {realPayments.map((p, i) => (
                    <div key={p.id || i} className="py-2 text-sm text-gray-700">
                      <div className="grid grid-cols-[1fr_140px_120px] gap-3">
                        <span>{fmtDate(p.paid_date || p.date)}</span>
                        <span>{p.method || 'Manual'}</span>
                        <span className="text-right">${formatCurrency(parseFloat(p.amount || 0))}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-900 mt-1 pt-2">
                  {Number(invoice.total_cc_fees_paid) > 0 ? (
                    <>
                      <div className="grid grid-cols-[1fr_120px] gap-3 text-sm text-gray-700 mt-1">
                        <span>Amount Paid</span>
                        <span className="text-right">${formatCurrency(invoice.amount_paid)}</span>
                      </div>
                      <div className="grid grid-cols-[1fr_120px] gap-3 text-sm text-blue-600 mt-1" data-testid="public-invoice-cc-fees-total">
                        <span>CC Processing Fee</span>
                        <span className="text-right">+${formatCurrency(invoice.total_cc_fees_paid)}</span>
                      </div>
                      <div className="grid grid-cols-[1fr_120px] gap-3 text-sm font-semibold text-gray-900 mt-1">
                        <span>Total Paid</span>
                        <span className="text-right">${formatCurrency(parseFloat(invoice.amount_paid || 0) + parseFloat(invoice.total_cc_fees_paid || 0))}</span>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-[1fr_120px] gap-3 text-sm font-semibold text-gray-900">
                      <span>Total Paid</span>
                      <span className="text-right">${formatCurrency(invoice.amount_paid)}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-[1fr_120px] gap-3 text-sm font-semibold text-gray-900 mt-1">
                    <span>Remaining Amount</span>
                    <span className="text-right">${formatCurrency(invoice.balance_due)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {scheduleItems.length > 0 && (
            <div className="mt-8" data-testid="public-invoice-payment-schedule">
              <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2" data-testid="public-invoice-payment-schedule-title">Payment Schedule</h3>
              <div className="grid grid-cols-[1fr_180px_90px] gap-3 text-xs font-semibold text-gray-500 border-b border-gray-200 pb-2" data-testid="public-invoice-payment-schedule-header">
                <span>Payment</span>
                <span className="text-right">Amount</span>
                <span className="text-right">Status</span>
              </div>
              <div className="divide-y divide-gray-200">
                {scheduleAllocations.map((item, index) => (
                  <div key={item.id || index} className="py-2 text-sm text-gray-700" data-testid={`public-invoice-payment-schedule-row-${index}`}>
                    <div className="grid grid-cols-[1fr_180px_90px] gap-3">
                      <span>{item.name || `Payment ${index + 1}`}</span>
                      <span className="text-right">{renderScheduleAmount(item)}</span>
                      <span className={`text-right font-medium ${item.balance <= 0 && item.due > 0 ? 'text-emerald-600' : item.paidAmount > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {item.balance <= 0 && item.due > 0
                          ? 'Paid'
                          : item.paidAmount > 0
                            ? 'Partial'
                            : 'Unpaid'}
                      </span>
                    </div>
                    {item.balance <= 0 && item.due > 0 && item.ccFeeFromPayment > 0 && (
                      <div className="text-xs text-blue-600 mt-0.5 text-right" data-testid={`public-invoice-schedule-cc-fee-${index}`}>
                        CC fee: +${formatCurrency(item.ccFeeFromPayment)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {invoice.notes && (
            <div className="mt-8" data-testid="public-invoice-notes">
              <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2" data-testid="public-invoice-notes-title">Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="public-invoice-notes-text">{invoice.notes}</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={() => window.open(`${API}/sales/invoices/public/${publicToken}/pdf?t=${Date.now()}`, '_blank')}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold"
            data-testid="public-invoice-download-pdf"
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

// Invoices List Module

const PublicChangeOrderView = ({ publicToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const signaturePadRef = useRef(null);
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const isPreviewMode = searchParams.get('mode') === 'preview';

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Include mode=preview to skip notifications for internal previews
      const params = new URLSearchParams();
      params.set('t', Date.now().toString());
      if (isPreviewMode) params.set('mode', 'preview');
      const response = await axios.get(`${API}/sales/change-orders/public/${publicToken}?${params.toString()}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading change order:', err);
      setError('Unable to load this change order.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [publicToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-change-order-loading">
        <div className="text-lg text-gray-600">Loading change order...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="public-change-order-error">
        <div className="text-lg text-red-600">{error || 'Change order not found'}</div>
      </div>
    );
  }

  const invoice = data.invoice || {};
  const changeOrder = data.change_order || {};
  const settings = data.settings || {};
  const totalValue = Number(invoice.total || 0);
  const changeOrderAmount = (Number(changeOrder.quantity || 0) * Number(changeOrder.unit_cost || 0))
    * (1 + (Number(changeOrder.markup_percentage || 0) / 100));
  const amountLabel = formatCurrency(changeOrderAmount);
  const changeOrderLabel = changeOrder.change_order_reason || changeOrder.description || 'Change Order';
  const requiresSignature = changeOrder.change_order_requires_signature !== false;
  const statusLabel = changeOrder.change_order_status === 'APPROVED'
    ? (changeOrder.change_order_signature
      ? (changeOrder.change_order_approved_method === 'manual' ? 'Approved (Manual + Signed)' : 'Approved (Signed)')
      : (changeOrder.change_order_approved_method === 'manual' ? 'Approved (Manual)' : 'Approved'))
    : 'Pending';
  const signatureSrc = changeOrder.change_order_signature
    ? (changeOrder.change_order_signature.startsWith('data:image')
      ? changeOrder.change_order_signature
      : `data:image/png;base64,${changeOrder.change_order_signature}`)
    : '';
  const hasSignatureImage = signatureSrc && signatureSrc.length > 50;

  const handleApproveChangeOrder = async (signatureData) => {
    const trimmedName = (signatureName || '').trim();
    if (requiresSignature && !trimmedName) {
      alert('Name is required.');
      return;
    }
    if (requiresSignature && !signatureData) {
      alert('Signature is required.');
      return;
    }
    if (!requiresSignature && signatureData && !trimmedName) {
      alert('Please enter your name to attach a signature.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/sales/change-orders/${publicToken}/approve`, {
        client_name: trimmedName,
        signature_data: signatureData
      });
      await loadData();
      setShowSignatureModal(false);
    } catch (err) {
      console.error('Error approving change order:', err);
      alert('Unable to approve change order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenSignatureModal = () => {
    setSignatureName(invoice.client_name || '');
    setShowSignatureModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-change-order-view">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg px-6 sm:px-10 py-8" data-testid="public-change-order-document">
          <div className="text-center text-xs tracking-[0.4em] text-gray-400 font-semibold">CHANGE ORDER</div>
          <div className="mt-6 flex flex-col md:flex-row md:justify-between gap-6">
            <div>
              <img
                src={`${API}/sales/logo`}
                alt={`${settings?.company_name || 'Company'} Logo`}
                className="h-12 object-contain"
                data-testid="public-change-order-company-logo"
              />
              <div className="mt-3 text-sm text-gray-700">
                <div className="font-semibold">{settings.company_name}</div>
                <div>{settings.company_address}</div>
                <div>{[settings.company_city, settings.company_province].filter(Boolean).join(', ')}</div>
                <div>{settings.company_phone}</div>
              </div>
            </div>
            <div className="text-sm text-gray-700 md:text-right">
              <div className="font-semibold" data-testid="public-change-order-client-name">{invoice.client_name}</div>
              <div>{invoice.client_address}</div>
              <div>{[invoice.client_city, invoice.client_province].filter(Boolean).join(', ')}</div>
              <div className="mt-3 text-xs text-gray-500">Invoice #{invoice.invoice_number}</div>
            </div>
          </div>

          <div className="mt-6 border border-gray-200 rounded-lg p-4" data-testid="public-change-order-summary">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800" data-testid="public-change-order-title">{changeOrderLabel}</div>
              <div className={`text-xs font-semibold ${changeOrder.change_order_status === 'APPROVED' ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="public-change-order-status">{statusLabel}</div>
            </div>
            <div className="mt-2 text-sm text-gray-600" data-testid="public-change-order-description">
              {changeOrder.description}
            </div>
            <div className="mt-3 text-sm font-semibold text-gray-800" data-testid="public-change-order-amount">Change Order Amount: ${amountLabel}</div>
            <div className="mt-1 text-xs text-gray-500" data-testid="public-change-order-note">
              This change order will be added to your existing invoice. New invoice total: ${formatCurrency(totalValue)}.
            </div>
          </div>

          {changeOrder.change_order_signature && (
            <div className="mt-6" data-testid="public-change-order-signature">
              <div className="text-xs text-gray-500">Signed by {changeOrder.change_order_signature_name || invoice.client_name || 'Customer'}{changeOrder.change_order_signed_at ? ` on ${new Date(changeOrder.change_order_signed_at).toLocaleDateString()}` : ''}</div>
              {hasSignatureImage ? (
                <img src={signatureSrc} alt="Signature" className="border border-gray-200 rounded mt-2 max-h-28" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : null}
            </div>
          )}

          {changeOrder.change_order_status !== 'APPROVED' && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid="public-change-order-actions">
              {requiresSignature ? (
                <button
                  onClick={handleOpenSignatureModal}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  data-testid="public-change-order-approve"
                >
                  Sign & Approve
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleApproveChangeOrder('')}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                    data-testid="public-change-order-approve"
                  >
                    Approve Change Order
                  </button>
                  <button
                    onClick={handleOpenSignatureModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold"
                    data-testid="public-change-order-approve-sign"
                  >
                    Add Signature (Optional)
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="public-change-order-signature-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-xl w-full p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Sign Change Order</h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-gray-500"
                data-testid="public-change-order-signature-close"
              >
                ✕
              </button>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="public-change-order-signature-name"
              />
            </div>
            <div className="mt-4 border border-gray-300 rounded-lg" data-testid="public-change-order-signature-canvas">
              <SignatureCanvas
                penColor="black"
                canvasProps={{ width: 520, height: 200, className: 'signature-canvas' }}
                ref={signaturePadRef}
              />
            </div>
            <div className="mt-4 flex justify-between">
              <button
                onClick={() => signaturePadRef.current?.clear()}
                className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700"
                data-testid="public-change-order-signature-clear"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  const signatureData = signaturePadRef.current?.isEmpty() ? '' : signaturePadRef.current?.toDataURL();
                  handleApproveChangeOrder(signatureData);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                data-testid="public-change-order-signature-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Approve'}
              </button>
            </div>
            {requiresSignature && (
              <div className="mt-3 text-xs text-gray-500">Signature is required to approve this change order.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Sales Clients Module

// Sales Reports Module



// Home Page Component

export { PublicEstimateView, PublicInvoiceView, PublicChangeOrderView };
